import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Tag,
  IconButton,
  Tooltip,
  useToast,
  HStack,
  Icon,
  Wrap,
  WrapItem,
  Input,
  Select,
  InputGroup,
  InputLeftElement,
  VStack,
  Switch,
  Center,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  FaEdit,
  FaBullhorn,
  FaInfoCircle,
  FaSearch
} from "react-icons/fa";
import Pagination from "react-js-pagination";

import Header from "../../components/Header";
import TemplateManagerSstats from "./Stats/TemplateManagerSstats";
import { fetchWrapper } from "../../helpers";
import { BsFillWalletFill, BsShieldFill } from "react-icons/bs";
import { toast } from "react-toastify";
import { LuBell, LuCopy, LuEye, LuMail, LuMessageCircle, LuMessageSquare, LuPhone } from "react-icons/lu";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";

import Prism from 'prismjs';
import "prismjs/components/prism-json";
import "prismjs/themes/prism-tomorrow.css";
import moment from "moment";
import { getUserTimeZone, hasPermission } from "../../utils/DefaultFunction";
import API from "../../helpers/axiosInstance";
import WhatsAppErrorModal from "./CreateTemplate/ErrorModal";
import { decodeHtmlEntities } from "./CreateTemplate/EmailDesigner/htmlExport";
import EmailPreviewModal from "./CreateTemplate/EmailPreviewModal";
import { parseWhatsAppMarkdown } from "./CreateTemplate/ChannelPreviewBox";

/**
 * Which field of `message_combine` holds the body for each channel.
 *
 * The preview used to guess: `msgCombine[name.toLowerCase().replace(/ /g,'_')]`, then
 * `msgCombine[name.toLowerCase() + 'Message']`, then `msgCombine.masterMessageContent`.
 * None of the guessed keys exist — the saved keys are `smsBody`, `pushBody`, `inAppBody`
 * and so on — so EVERY channel fell through to masterMessageContent, which TemplateManagerNew
 * sets to `whatsappBody`. That is why all six tabs showed the WhatsApp message.
 *
 * Keys verified against the save payload in TemplateManagerNew.jsx and the legacy
 * CreateTemplate.jsx, which agree on these names.
 */
const CHANNEL_CONTENT_FIELDS = {
  "Whatsapp": ["whatsappBody"],
  "WhatsApp": ["whatsappBody"],
  "SMS": ["smsBody"],
  "Push Notification": ["pushBody"],
  "In-App Notification": ["inAppBody"],
  "Voice (IVR)": ["voiceScript"],
  "E-Mail": ["emailHtml", "html_code"],
};

/**
 * Body for one channel, or '' when that channel has nothing configured.
 *
 * Deliberately NO fallback to masterMessageContent: it is a copy of the WhatsApp body,
 * so falling back to it is what produced the wrong content rather than an honest gap.
 */
function channelContentFrom(msgCombine, channelName) {
  const fields = CHANNEL_CONTENT_FIELDS[channelName] || [];
  for (const field of fields) {
    const value = msgCombine?.[field];
    if (typeof value === "string" && value.trim()) return value;
  }
  // Push carries its headline separately; show it when there is no body.
  if (channelName === "Push Notification" && msgCombine?.pushTitle?.trim()) {
    return msgCombine.pushTitle;
  }
  return "";
}

/**
 * The email body of a template, ready for EmailPreviewModal — '' when the template has
 * none, which is what hides the row's preview button.
 *
 * emailHtml/html_code hold the designed email; masterMessageContent is only used when it
 * actually contains markup, since for a WhatsApp/SMS template it is plain copy.
 */
function emailHtmlFrom(msgCombine, template) {
  const candidates = [
    msgCombine?.emailHtml,
    msgCombine?.html_code,
    template?.html_code,
    msgCombine?.masterMessageContent,
  ];
  for (const raw of candidates) {
    if (typeof raw !== "string" || !raw.trim()) continue;
    const decoded = decodeHtmlEntities(raw);
    if (/<[^>]*>/.test(decoded)) return decoded;
  }
  return "";
}

const getApiHeaders = () => ({
  'x-api-key': localStorage.getItem('api-key'),
  'x-access-token': localStorage.getItem('customer-portal'),
});

const TemplateManager = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const toastUI = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize from URL params
  const initialQuery = searchParams.get("query") || "";
  const initialChannel = searchParams.get("channel_id") || "";
  const initialType = searchParams.get("type_id") || "";
  const initialStatus = searchParams.get("status_id") || "1";
  const initialWhatsAppStatus = searchParams.get("whatsapp_status_id") || "";
  const initialPage = Number(searchParams.get("page")) || 1;

  // State declarations
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncloading, setSyncLoading] = useState(false);
  const [fetchWALoading, setFetchWALoading] = useState(false);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [customerList, setCustomerList] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // { subject, body } while the master HTML content is being previewed; null when closed.
  const [htmlPreview, setHtmlPreview] = useState(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [templateHistory, setTemplateHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedTemplateForHistory, setSelectedTemplateForHistory] = useState(null);
  const [messageTypes, setMessageTypes] = useState([]);
  const [whatsAppStatusList, setWhatsAppStatusList] = useState([]);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [selectedChannel, setSelectedChannel] = useState(initialChannel);
  const [selectedCustomer, setSelectedCustomer] = useState(searchParams.get("customer_id") || "");
  const [selectedType, setSelectedType] = useState(initialType);
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [selectedWhatsAppStatus, setSelectedWhatsAppStatus] = useState(initialWhatsAppStatus);
  const [activePage, setActivePage] = useState(initialPage);
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [cloneLoadingId, setCloneLoadingId] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [isWhatsAppErrorModalOpen, setIsWhatsAppErrorModalOpen] = useState(false);
  const [selectedWhatsAppError, setSelectedWhatsAppError] = useState(null);
  const [stats, setStats] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Fetch message types for filter dropdown
  const fetchMessageTypes = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/message-type/list`;
      const data = await fetchWrapper.get(url);
      setMessageTypes(data.data || []);
    } catch (error) {
      console.error("Error loading message types:", error);
    }
  };

  // Fetch WhatsApp status list
  const fetchWhatsAppStatusList = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/whatsapp-status/list`;
      const data = await fetchWrapper.get(url);
      setWhatsAppStatusList(data.data || []);
    } catch (error) {
      console.error("Error loading WhatsApp status:", error);
    }
  };

  // Load customers
  const LoadCustomers = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/customer/list`;
      const data = await fetchWrapper.get(url);
      setCustomerList(data.data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Failed to load customers');
    }
  };

  // Fetch permissions
  const fetchPermissions = async () => {
    try {
      const params = location.pathname.replace('/', "");
      const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${params}`;
      const response = await API.get(url, { headers: getApiHeaders() });
      setPermissions(response.data.data || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setPermissions([]);
    }
  };

  // Fetch templates
  const fetchTemplates = async (page = 1, filters = {}) => {
    setTableLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString(),
      });

      if (filters.query?.trim()) params.append("query", filters.query.trim());
      if (filters.channel_id) params.append("channel_id", filters.channel_id);
      if (filters.type_id) params.append("message_type_id", filters.type_id);
      if (filters.status_id) params.append("status_id", filters.status_id);
      if (filters.whatsapp_status_id) params.append("whatsapp_status_id", filters.whatsapp_status_id);

      const url = `${process.env.REACT_APP_API_URL}/template-message/list?${params.toString()}`;
      const data = await fetchWrapper.get(url);

      setTemplates(data.data || []);
      setTotalItems(data.pagination?.total || 0);
    } catch (error) {
      console.error("Error loading templates:", error);
      toastUI({
        title: "Error",
        description: "Failed to load templates",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setTableLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/template-message/list-stat`;
      const data = await fetchWrapper.get(url);
      setStats(data.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toastUI({
        title: "Error",
        description: "Failed to load template stats",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Fetch Template History
  const fetchTemplateHistory = async (templateId) => {
    setHistoryLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/template-message/tempalte-history?template_id=${templateId}`;
      const data = await fetchWrapper.get(url);

      if (data.success) {
        setTemplateHistory(data.data || []);
      } else {
        toast.error(data.message || "Failed to load template history");
      }
    } catch (error) {
      console.error("Error loading template history:", error);
      toast.error("Failed to load template history");
    } finally {
      setHistoryLoading(false);
    }
  };

  // Update search params
  const updateSearchParams = (params) => {
    const newParams = new URLSearchParams();

    if (params.query) newParams.set("query", params.query);
    if (params.channel_id) newParams.set("channel_id", params.channel_id);
    if (params.type_id) newParams.set("type_id", params.type_id);
    if (params.status_id) newParams.set("status_id", params.status_id);
    if (params.whatsapp_status_id) newParams.set("whatsapp_status_id", params.whatsapp_status_id);
    if (params.page) newParams.set("page", params.page);

    setSearchParams(newParams);
  };

  // Handle sync
  const handleSync = async () => {
    setSyncLoading(true);
    try {
      const params = new URLSearchParams();

      if (selectedChannel) params.append("channel_id", selectedChannel);
      if (selectedType) params.append("type_id", selectedType);
      if (selectedStatus) params.append("status_id", selectedStatus);
      if (selectedWhatsAppStatus) params.append("whatsapp_status_id", selectedWhatsAppStatus);
      params.append("page", activePage || 1);

      const url = `${process.env.REACT_APP_API_URL}/template-message/get-quality?${params.toString()}`;
      const data = await fetchWrapper.get(url);

      if (data.success) {
        setTemplates([])
        toast.success("Successfully Synced");
        fetchTemplates(activePage, {
          query: searchQuery,
          channel_id: selectedChannel,
          type_id: selectedType,
          status_id: selectedStatus,
          whatsapp_status_id: selectedWhatsAppStatus,
        });
      } else {
        toast.error(data.message || "Failed to sync templates");
      }
    } catch (error) {
      console.error("Error syncing templates:", error);
      toast.error("Something went wrong while syncing");
    } finally {
      setSyncLoading(false);
    }
  };

  const handleFetchWhatsApp = async () => {
    setFetchWALoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/template-message/sync-from-whatsapp`;
      const data = await fetchWrapper.get(url);
      if (data.success) {
        toast.success("WhatsApp templates fetched successfully");
        fetchTemplates(activePage, {
          query: searchQuery,
          channel_id: selectedChannel,
          type_id: selectedType,
          status_id: selectedStatus,
          whatsapp_status_id: selectedWhatsAppStatus,
        });
      } else {
        toast.error(data.message || "Failed to fetch WhatsApp templates");
      }
    } catch (error) {
      console.error("Error fetching WhatsApp templates:", error);
      toast.error("Something went wrong while fetching WhatsApp templates");
    } finally {
      setFetchWALoading(false);
    }
  };

  // Handle History Modal Open
  const handleHistoryClick = (template) => {
    setSelectedTemplateForHistory(template);
    setIsHistoryModalOpen(true);
    fetchTemplateHistory(template.id);
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setActivePage(pageNumber);
    const filters = {
      query: searchQuery,
      channel_id: selectedChannel,
      type_id: selectedType,
      status_id: selectedStatus,
      whatsapp_status_id: selectedWhatsAppStatus,
      page: pageNumber,
    };
    updateSearchParams(filters);
    fetchTemplates(pageNumber, filters);
  };

  // Handle preview
  const handlePreview = (template) => {
    setSelectedTemplate(template);
    setIsPreviewOpen(true);
  };

  // Handle status toggle
  const handleStatusToggle = async (template) => {
    try {
      const newStatus = template.status_id === 1 ? 0 : 1;

      const payload = {
        id: template.id.toString(),
        status_id: newStatus.toString(),
      };
      setStatusLoadingId(template.id);

      const url = `${process.env.REACT_APP_API_URL}/template-message/change-status`;
      const res = await fetchWrapper.post(url, payload);

      if (res.success) {
        toastUI({
          title: "Success",
          description: `Template "${template.name}" is now ${newStatus === 1 ? "Active" : "Inactive"}.`,
          status: "success",
          duration: 2000,
          isClosable: true,
        });

        setTemplates((prev) =>
          prev.map((t) =>
            t.id === template.id ? { ...t, status_id: newStatus } : t
          )
        );

        fetchStats();
        fetchTemplates(activePage, {
          query: searchQuery,
          channel_id: selectedChannel,
          type_id: selectedType,
          status_id: selectedStatus,
          whatsapp_status_id: selectedWhatsAppStatus,
        });
      } else {
        toastUI({
          title: "Error",
          description: res.message || "Failed to update status",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Status change error:", error);
      toastUI({
        title: "Error",
        description: "Something went wrong while updating status",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setStatusLoadingId(null);
    }
  };

  // Handle WhatsApp error click
  const handleWhatsappErrorClick = (template) => {
    setSelectedWhatsAppError(template);
    setIsWhatsAppErrorModalOpen(true);
  };

  // Handle clone
  const handleClone = async (template) => {
    setCloneLoadingId(template.id);
    try {
      const url = `${process.env.REACT_APP_API_URL}/template-message/clone`;
      const res = await fetchWrapper.post(url, { template_id: template.id });

      if (res.success) {
        toast.success(`Template "${template.name}" cloned successfully`);
        fetchTemplates(activePage, {
          query: searchQuery,
          channel_id: selectedChannel,
          type_id: selectedType,
          status_id: selectedStatus,
          whatsapp_status_id: selectedWhatsAppStatus,
        });
      } else {
        toast.error(res.message || "Failed to clone template");
      }
    } catch (error) {
      console.error("Clone error:", error);
      toast.error("Something went wrong while cloning");
    } finally {
      setCloneLoadingId(null);
    }
  };

  // SINGLE INITIALIZATION - Runs ONCE on mount
  useEffect(() => {
    if (isInitialized) return;

    const initializeApp = async () => {
      setLoading(true);
      
      try {
        // Fetch all reference data in parallel
        await Promise.all([
          fetchMessageTypes(),
          fetchWhatsAppStatusList(),
          LoadCustomers(),
          fetchPermissions(),
        ]);

        // Fetch templates and stats with initial filters
        const filters = {
          query: initialQuery,
          channel_id: initialChannel,
          type_id: initialType,
          status_id: initialStatus,
          whatsapp_status_id: initialWhatsAppStatus,
        };

        // await Promise.all([
        //   fetchTemplates(initialPage, filters),
        //   fetchStats(),
        // ]);

        setIsInitialized(true);
      } catch (error) {
        console.error("Initialization error:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle filter changes ONLY after initialization
  useEffect(() => {
    if (!isInitialized) return;

    const filters = {
      query: searchQuery,
      channel_id: selectedChannel,
      type_id: selectedType,
      status_id: selectedStatus,
      whatsapp_status_id: selectedWhatsAppStatus,
    };

    const delay = searchQuery !== initialQuery ? 500 : 0;

    const timeoutId = setTimeout(() => {
      updateSearchParams({ ...filters, page: activePage });
      fetchTemplates(activePage, filters);
      fetchStats();
    }, delay);

    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedChannel, selectedType, selectedStatus, selectedWhatsAppStatus, activePage, isInitialized]);

  // Message type configuration
  const messageTypeConfig = {
    OTP: { icon: BsShieldFill, color: "red.500" },
    Transaction: { icon: BsFillWalletFill, color: "green.500" },
    Marketing: { icon: FaBullhorn, color: "orange.400" },
    Informational: { icon: FaInfoCircle, color: "purple.500" },
  };

  function MessageTypeCell({ type }) {
    // DA-BUG-0265 — `type` can be null when the join in
    // /template-message/list returns a template whose message_type_id
    // doesn't match any active row in tbl_message_type (deleted /
    // disabled / legacy template). Fall back to a neutral cell rather
    // than crashing the entire Template Manager page.
    const name = type?.name;
    if (!name) {
      return (
        <HStack>
          <Icon as={FaInfoCircle} boxSize={3} color="gray.500" />
          <Text fontWeight="medium" color="gray.500">—</Text>
        </HStack>
      );
    }
    const { icon, color } = messageTypeConfig[name] || {
      icon: FaInfoCircle,
      color: "gray.500",
    };
    return (
      <HStack>
        <Icon as={icon} boxSize={3} color={color} />
        <Text fontWeight="medium">{name}</Text>
      </HStack>
    );
  }

  // Available channels
  const availableChannels = [
    {
      name: "Whatsapp",
      icon: LuMessageCircle,
      color: "#00a63e",
      description: "Rich messaging with media and buttons",
      id: 2,
    },
    {
      name: "SMS",
      icon: LuMessageSquare,
      color: "#155dfc",
      description: "Simple text messages",
      id: 1,
    },
    {
      name: "E-Mail",
      icon: LuMail,
      color: "#9810fa",
      description: "HTML emails with rich content",
      id: 3,
    },
    {
      name: "Push Notification",
      icon: LuBell,
      color: "#e60076",
      description: "Mobile app notifications",
      id: 6,
    },
    {
      name: "In-App Notification",
      icon: LuBell,
      color: "#4f39f6",
      description: "In-app messages and alerts",
      id: 5,
    },
    {
      name: "Voice (IVR)",
      icon: LuPhone,
      color: "#f54a00",
      description: "Automated voice calls",
      id: 4,
    },
  ];

  function SelectedChannels({ selectedChannels }) {
    const filteredChannels = availableChannels.filter(channel =>
      selectedChannels?.includes(channel.name)
    );

    return (
      <Wrap spacing={4}>
        {filteredChannels.map(channel => (
          <WrapItem key={channel.id}>
            <HStack spacing={1}>
              <Icon as={channel.icon} boxSize={3} color={`${channel.color}`} />
            </HStack>
          </WrapItem>
        ))}
      </Wrap>
    );
  }

  // Check permissions
  const listPermission = hasPermission("template-list", permissions);
  const createPermission = hasPermission("template-create", permissions);
  const syncPermission = hasPermission("template-sync", permissions);
  const editPermission = hasPermission("template-edit", permissions);
  const viewPermission = hasPermission("template-view", permissions);
  const switchPermission = hasPermission("template-active-inactive", permissions);

  if (loading) {
    return (
      <>
        <Header />
        <Box p={8} border={"1px solid #0000001a"} m={8} bg={"white"} borderRadius={"15px"}>
          <Flex justify="center" align="center" h="300px">
            <Spinner size="lg" />
          </Flex>
        </Box>
      </>
    );
  }
  return (
    <>
      <Header />

      <Box p={8} border={"1px solid #0000001a"} m={8} bg={"white"} borderRadius={"15px"}>
        <Flex justify="space-between" align="center" mb={6}>
          <Box>
            <Text fontSize="2xl" fontWeight="bold">
              Template Manager
            </Text>
            <Text fontSize="sm" color="gray.600">
              Create and manage notification templates across all channels
            </Text>
          </Box>
          <div>
            {syncPermission && (<Button
              bg="var(--brand-primary)"
              borderRadius="md"
              style={{ marginRight: "1rem" }}
              size={"sm"}
              onClick={handleSync}
              isLoading={syncloading}
              color={"white"}
            >
              Sync
            </Button>)}
            <Button
              bg="var(--brand-primary)"
              borderRadius="md"
              style={{ marginRight: "1rem" }}
              size={"sm"}
              onClick={handleFetchWhatsApp}
              isLoading={fetchWALoading}
              color={"white"}
            >
              Fetch WhatsApp Templates
            </Button>
            {
              createPermission && (

                <Button
                  bg="var(--brand-primary)"
                  borderRadius="md"
                  onClick={() => navigate("/template-add")}
                  size={"sm"}
                  color={"white"}
                >
                  Create Template
                </Button>
              )
            }
          </div>
        </Flex>
        {listPermission ? <><TemplateManagerSstats stats={stats} />

          <Box className="box-style-card" mb={5}>
            <Flex gap={4} align="end" wrap="wrap">
              <Box flex="1" minW="200px">
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  Search Templates
                </Text>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" display={"flex"} justifyContent={"center"} alignItems={"center"}>
                    <Icon as={FaSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search templates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    bg="white"
                    size="sm"
                  />
                </InputGroup>
              </Box>

              <Box minW="150px">
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  All Channels
                </Text>
                <Select
                  value={selectedChannel}
                  onChange={(e) => setSelectedChannel(e.target.value)}
                  bg="white"
                  size="sm"
                >
                  <option value="">All Channels</option>
                  {availableChannels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.name}
                    </option>
                  ))}
                </Select>
              </Box>

              <Box minW="150px">
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  All Types
                </Text>
                <Select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  bg="white"
                  size="sm"
                >
                  <option value="">All Types</option>
                  {messageTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Select>
              </Box>

              <Box minW="150px">
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  All Status
                </Text>
                <Select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  bg="white"
                  size="sm"
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </Select>
              </Box>

              <Box minW="150px">
                <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                  Whatsapp Status
                </Text>
                <Select
                  value={selectedWhatsAppStatus}
                  onChange={(e) => setSelectedWhatsAppStatus(e.target.value)}
                  bg="white"
                  size="sm"
                >
                  <option value="">All Status</option>
                  {whatsAppStatusList.map((waRs) => (
                    <option key={waRs.id} value={waRs.id}>
                      {waRs.name}
                    </option>
                  ))}
                </Select>
              </Box>
            </Flex>

            {(searchQuery || selectedChannel || selectedType || selectedStatus || selectedWhatsAppStatus) && (
              <Flex mt={3} gap={2} align="center" wrap="wrap">
                <Text fontSize="xs" color="gray.600">Active filters:</Text>
                {searchQuery && (
                  <Tag size="sm" colorScheme="blue" variant="subtle">
                    Search: {searchQuery}
                  </Tag>
                )}
                {selectedChannel && (
                  <Tag size="sm" colorScheme="green" variant="subtle">
                    Channel: {availableChannels.find(c => c.id.toString() === selectedChannel)?.name}
                  </Tag>
                )}
                {selectedType && (
                  <Tag size="sm" colorScheme="purple" variant="subtle">
                    Type: {messageTypes.find(t => t.id.toString() === selectedType)?.name}
                  </Tag>
                )}
                {selectedStatus && (
                  <Tag size="sm" colorScheme="orange" variant="subtle">
                    Status: {selectedStatus === "1" ? "Active" : "Inactive"}
                  </Tag>
                )}
                {selectedWhatsAppStatus && (
                  <Tag size="sm" colorScheme="orange" variant="subtle">
                    Whatsapp Status: {whatsAppStatusList.find(t => t.id.toString() === selectedWhatsAppStatus)?.name}
                  </Tag>
                )}
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedChannel("");
                    setSelectedType("");
                    setSelectedStatus("");
                    setSelectedWhatsAppStatus("");
                  }}
                >
                  Clear all
                </Button>
              </Flex>
            )}
          </Box>

          {loading ? (
            <Flex justify="center" align="center" minH="200px">
              <Spinner size="xl" />
            </Flex>
          ) : (
            <>
              <Box overflowX="auto" border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th textAlign="start" fontSize="sm">Name</Th>
                      <Th textAlign="start" fontSize="sm">Type</Th>
                      <Th textAlign="start" fontSize="sm">Channel</Th>
                      <Th textAlign="center" fontSize="sm">Status</Th>
                      <Th textAlign="start" fontSize="sm">Created</Th>
                      <Th textAlign="start" fontSize="sm">Actions</Th>
                    </Tr>
                  </Thead>

                  <Tbody>
                    {tableLoading ? (
                      <Tr>
                        <Td colSpan={6}>
                          <Flex justify="center" align="center" py={10}>
                            <Spinner size="lg" color="blue.500" />
                          </Flex>
                        </Td>
                      </Tr>
                    ) : templates.length > 0 ? (
                      templates.map((template) => {
                        let msgCombine = {};
                        try {
                          msgCombine = template.message_combine
                            ? JSON.parse(template.message_combine)
                            : {};
                        } catch (e) {
                          console.error(`Failed to parse message_combine for template ${template.id}:`, e);
                        }

                        // computed once per row: decodeHtmlEntities builds a DOM node,
                        // so it should not run twice (condition + click handler)
                        const emailHtml = emailHtmlFrom(msgCombine, template);

                        return (
                          <Tr key={template.id}>
                            <Td textAlign="start" fontSize="sm">
                              <Text fontWeight="normal" fontSize="sm">
                                {template.name}
                              </Text>
                              {/* <Text fontSize="xs" color="gray.500">
                                {msgCombine?.description || "-"}
                              </Text> */}
                              <HStack spacing={2} mt={1}>
                                {(template?.WhatsAppStatus && JSON.parse(template?.channel_id).includes(2)) && (
                                  <Tag colorScheme={template?.WhatsAppStatus.color} size="sm">
                                    {template?.WhatsAppStatus.name}
                                  </Tag>
                                )}

                                {template?.whatsapp_status_id === 14 && (<Tag
                                  bg={"#f5b3b3ff"}
                                  color={"rgba(250, 187, 187, 1)ff"}
                                  border={"1px solid"}
                                  borderColor={"#0000001a"}
                                  cursor={"pointer"}
                                  _hover={{ bg: "#fef3c6" }}
                                  size="sm"
                                  onClick={() => handleWhatsappErrorClick(template)}
                                >
                                  Whatsapp Error
                                </Tag>)}
                                {template?.WhatsAppMessageType && (
                                  <Tag
                                    bg={"#ffedd4"}
                                    color={"#bb4d00"}
                                    border={"1px solid"}
                                    borderColor={"#0000001a"}
                                    // cursor={"pointer"}
                                    // _hover={{ bg: "#fef3c6" }}
                                    size="sm"
                                  // onClick={() => handleHistoryClick(template)}
                                  >
                                    {template?.WhatsAppMessageType?.name}
                                  </Tag>
                                )}

                                {template?.change_category && (<Tag
                                  bg={"#fffbeb"}
                                  color={"#bb4d00"}
                                  border={"1px solid"}
                                  borderColor={"#0000001a"}
                                  cursor={"pointer"}
                                  _hover={{ bg: "#fef3c6" }}
                                  size="sm"
                                  onClick={() => handleHistoryClick(template)}
                                >
                                  Category Changed
                                </Tag>)}



                              </HStack>
                            </Td>

                            <Td textAlign="start" fontSize="sm">
                              {MessageTypeCell({ type: template.MessageType })}
                            </Td>

                            <Td textAlign="start" fontSize="sm">
                              <SelectedChannels selectedChannels={msgCombine?.selectedChannels} />
                            </Td>

                            <Td textAlign="center" fontSize="sm">
                              <VStack spacing={1} align="center" justify="center" w="100%">
                                <Tag
                                  colorScheme={template.status_id === 1 ? "green" : "red"}
                                  size="sm"
                                  textAlign="center"
                                >
                                  {template.status_id === 1 ? "Active" : "Inactive"}
                                </Tag>
                                {switchPermission && (
                                  <Switch
                                    size="sm"
                                    colorScheme="green"
                                    isChecked={template.status_id === 1}
                                    onChange={() => handleStatusToggle(template)}
                                    disabled={statusLoadingId}
                                  />
                                )}

                                {statusLoadingId === template.id && <Spinner size="xs" color="rgb(var(--brand-primary-rgb) / 0.902)" />}
                              </VStack>
                            </Td>

                            <Td textAlign="start" fontSize="sm">
                              {moment
                                .utc(template.createdAt)
                                .tz(getUserTimeZone())
                                .format("DD MMM YY hh:mm:ss A")}
                            </Td>

                            <Td textAlign="start" fontSize="sm">
                              <Flex gap={2} alignItems="center">
                                {viewPermission && (

                                  <IconButton
                                    aria-label="Preview Template"
                                    icon={<LuEye />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handlePreview(template)}
                                  />
                                )}
                                {/* Straight to the rendered email, same modal the template
                                    preview's "Master Message Content" opens — one click
                                    instead of preview modal -> Preview HTML. Hidden for
                                    templates with no HTML body. */}
                                {viewPermission && emailHtml && (
                                  <Tooltip label="Preview HTML email" hasArrow placement="top">
                                    <IconButton
                                      aria-label="Preview HTML email"
                                      icon={<LuMail />}
                                      size="sm"
                                      variant="ghost"
                                      color="var(--brand-primary)"
                                      onClick={() =>
                                        setHtmlPreview({
                                          subject: msgCombine?.emailSubject || template.name,
                                          body: emailHtml,
                                        })
                                      }
                                    />
                                  </Tooltip>
                                )}
                                {editPermission && (

                                  <IconButton
                                    aria-label="Edit Template"
                                    icon={<FaEdit />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => navigate(`/template-edit/${template.id}`)}
                                  />
                                )}
                                <IconButton
                                  aria-label="Clone Template"
                                  icon={cloneLoadingId === template.id ? <Spinner size="xs" /> : <LuCopy />}
                                  size="sm"
                                  variant="ghost"
                                  isDisabled={!!cloneLoadingId}
                                  onClick={() => handleClone(template)}
                                />
                              </Flex>
                            </Td>
                          </Tr>
                        );
                      })
                    ) : (
                      <Tr>
                        <Td colSpan={7} textAlign="center" fontSize="sm" color="gray.500">
                          No templates found
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>

              {totalItems > itemsPerPage && (
                <Flex justify="center" mt={4}>
                  <Pagination
                    activePage={activePage}
                    itemsCountPerPage={itemsPerPage}
                    totalItemsCount={totalItems}
                    pageRangeDisplayed={5}
                    onChange={handlePageChange}
                    itemClass="page-item"
                    linkClass="page-link"
                  />
                </Flex>
              )}

              {/* Preview Modal */}
              <Modal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} size="2xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>Template Preview</ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    {selectedTemplate && (() => {
                      let msgCombine = {};
                      try {
                        msgCombine = selectedTemplate.message_combine
                          ? JSON.parse(selectedTemplate.message_combine)
                          : {};
                      } catch (e) {
                        console.error(`Failed to parse message_combine for template ${selectedTemplate.id}:`, e);
                      }

                      const selectedChannels = msgCombine.selectedChannels || [];

                      // For an email template the master content is a whole HTML document,
                      // and printing it verbatim filled the modal with source. Offer a
                      // preview instead; plain-text channels still show their text inline.
                      const masterRaw = msgCombine.masterMessageContent;
                      const masterContent = typeof masterRaw === "string" ? decodeHtmlEntities(masterRaw) : masterRaw;
                      const masterIsHtml = typeof masterContent === "string" && /<[^>]*>/.test(masterContent);

                      const getChannelIcon = (name) => {
                        const channel = availableChannels.find((ch) => ch.name === name);
                        return channel ? { icon: channel.icon, color: channel.color } : {};
                      };

                      let newObj = {};

                      for (let i = 0; i < msgCombine.variables?.length; i++) {
                        const variable = msgCombine.variables[i];
                        newObj[variable.name] = `${variable.name} goes here`;
                      }

                      const isIvr =
                        msgCombine.selectedChannels?.includes('Voice (IVR)') ||
                        (msgCombine.selectedChannelIds || []).includes(4);

                      const jsonData = {
                        to: "string",
                        email: "string (Optional)",
                        message_template_id: selectedTemplate?.id,
                        preferred_channel: `${msgCombine.selectedChannels.join(" / ")} (Remove this key when using smart/custom routing)`,
                        ...newObj,
                      };

                      return (
                        <Box>
                          <Flex>
                            <Box mb={4} width={"50%"}>
                              <Text fontWeight="semibold">Template Information</Text>
                              <Text fontSize="sm" color="gray.600">Name: <strong>{selectedTemplate.name}</strong></Text>
                              <Text fontSize="sm" color="gray.600">Template Id: <strong>{selectedTemplate.id}</strong></Text>
                              <Text fontSize="sm" color="gray.600">Type: <strong>{selectedTemplate.MessageType?.name}</strong></Text>
                              <Text fontSize="sm" color="gray.600">Status: <strong>{selectedTemplate.status_id === 1 ? "Active" : "Inactive"}</strong></Text>
                              <Text fontSize="sm" color="gray.600"><Flex alignItems={"center"}><span style={{ marginRight: ".5rem" }}>Channels:</span> <SelectedChannels selectedChannels={msgCombine.selectedChannels} /></Flex></Text>
                            </Box>
                            {msgCombine.variables?.length > 0 && (
                              <Box mb={4}>
                                <Text fontWeight="semibold" mb={1}>Variables</Text>
                                <Wrap>
                                  {msgCombine.variables.map((v, i) => (
                                    <Tag key={i} size="sm" bg={"white"} border={"1px solid"} borderColor={"gray.200"}>{`{{${v.name}}}`}</Tag>
                                  ))}
                                </Wrap>
                              </Box>
                            )}
                          </Flex>

                          {masterContent && (
                            <Box mb={6}>
                              <Text fontWeight="semibold" mb={2}>
                                Master Message Content
                              </Text>
                              <Box
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="md"
                                p={3}
                                bg="gray.50"
                              >
                                {masterIsHtml ? (
                                  <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
                                    <Text fontSize="sm" color="gray.600">
                                      HTML email content · {masterContent.length.toLocaleString()} characters
                                    </Text>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      leftIcon={<LuEye />}
                                      flexShrink={0}
                                      onClick={() =>
                                        setHtmlPreview({
                                          subject: msgCombine.emailSubject || "",
                                          body: masterContent,
                                        })
                                      }
                                    >
                                      Preview HTML
                                    </Button>
                                  </Flex>
                                ) : (
                                  <Text fontSize="sm" whiteSpace="pre-wrap">
                                    {/* masterMessageContent is always a copy of the WhatsApp body (see
                                        note above), so it carries the same bold/italic/strike/mono markup
                                        and needs the same rendering. */}
                                    {parseWhatsAppMarkdown(masterContent)}
                                  </Text>
                                )}
                              </Box>
                            </Box>
                          )}

                          <Text fontWeight="semibold" my={2}>
                            Payload
                          </Text>
                          <pre
                            style={{
                              backgroundColor: "black",
                              textShadow: "none",
                              color: "white",
                              padding: "0px",
                              borderRadius: "8px",
                              fontFamily: "monospace",
                            }}
                          >
                            <span
                              style={{
                                padding: 15,
                                backgroundColor: "#2F2F2F",
                                fontSize: "14px",
                                display: "block",
                                marginBottom: "10px",
                              }}
                            >
                              json
                            </span>
                            <code
                              className="language-json"
                              style={{ padding: "0px 15px 15px", display: "block", whiteSpace: "pre-wrap" }}
                              dangerouslySetInnerHTML={{ __html: Prism.highlight(JSON.stringify(jsonData, null, 2), Prism.languages.json, 'json') }}
                            />
                          </pre>

                          <VStack align="stretch" spacing={3} mt={4}>
                            {selectedChannels.map((channelName, idx) => {
                              const { icon: IconComp, color } = getChannelIcon(channelName);

                              const rawChannelMessage = channelContentFrom(msgCombine, channelName);

                              // Some backends store the content entity-escaped; decoding is a
                              // no-op on already-raw HTML, so it is safe either way and makes
                              // the isHtml test below see the real markup.
                              const channelMessage =
                                typeof rawChannelMessage === "string"
                                  ? decodeHtmlEntities(rawChannelMessage)
                                  : rawChannelMessage;

                              const isHtml =
                                typeof channelMessage === "string" &&
                                /<[^>]*>/.test(channelMessage);

                              return (
                                <Box key={idx}>
                                  <HStack spacing={2} mb={2}>
                                    {IconComp && <Icon as={IconComp} color={color} boxSize={5} />}
                                    <Text fontWeight="semibold">
                                      {channelName}
                                    </Text>
                                  </HStack>

                                  {isHtml ? (
                                    // Same treatment as Master Message Content above: an email
                                    // template is a whole HTML document, so it opens on demand
                                    // rather than taking 520px of the modal per channel.
                                    //
                                    // ET_049: whichever way it is shown, it must NOT go through
                                    // dangerouslySetInnerHTML — that drops the email markup into the
                                    // app's own DOM, where the global rules in index.css apply to it.
                                    // `table { width:100% }` overrode the button/social wrappers'
                                    // `display:inline-table` and stretched them edge to edge, and
                                    // `td { padding:10px }` spread the social icons across the full
                                    // width. The preview modal renders it in an iframe — its own
                                    // document — so it looks exactly as it will in the inbox.
                                    <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3} bg="white" boxShadow="sm">
                                      <Flex align="center" justify="space-between" gap={3} flexWrap="wrap">
                                        <Text fontSize="sm" color="gray.600">
                                          HTML email content · {channelMessage.length.toLocaleString()} characters
                                        </Text>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          leftIcon={<LuEye />}
                                          flexShrink={0}
                                          onClick={() =>
                                            setHtmlPreview({
                                              subject: msgCombine.emailSubject || "",
                                              body: channelMessage,
                                            })
                                          }
                                        >
                                          Preview HTML
                                        </Button>
                                      </Flex>
                                    </Box>
                                  ) : (
                                    <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={3} bg="white" boxShadow="sm">
                                      {channelMessage.trim() ? (
                                        <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">
                                          {/* WhatsApp's own body markup (*bold*, _italic_, ~strike~, ```mono```)
                                              is stored verbatim — it's only ever meant to be rendered, never
                                              read as literal asterisks/underscores, same as the create-page
                                              preview (WhatsAppPreview) already does. */}
                                          {channelName === "Whatsapp" || channelName === "WhatsApp"
                                            ? parseWhatsAppMarkdown(channelMessage)
                                            : channelMessage}
                                        </Text>
                                      ) : (
                                        // Say so plainly. Showing the WhatsApp body here — the old
                                        // masterMessageContent fallback — hid genuinely unconfigured
                                        // channels behind content that was never theirs.
                                        <Text fontSize="sm" color="gray.400" fontStyle="italic">
                                          No content configured for this channel.
                                        </Text>
                                      )}
                                      {channelName === "Push Notification" && msgCombine.pushTitle?.trim() && msgCombine.pushBody?.trim() && (
                                        <Text fontSize="xs" color="gray.500" mt={2}>
                                          Title: {msgCombine.pushTitle}
                                        </Text>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </VStack>
                        </Box>
                      );
                    })()}
                  </ModalBody>

                  <ModalFooter>
                    <Button onClick={() => setIsPreviewOpen(false)}>Close</Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>

              {/* Master HTML content, opened from "Preview HTML" above. Stacks over the
                  template preview modal, which stays open behind it. */}
              <EmailPreviewModal
                isEmailPreviewOpen={!!htmlPreview}
                onEmailPreviewClose={() => setHtmlPreview(null)}
                subject={htmlPreview?.subject}
                body={htmlPreview?.body}
              />

              {/* Template History Modal */}
              <Modal
                isOpen={isHistoryModalOpen}
                onClose={() => {
                  setIsHistoryModalOpen(false);
                  setTemplateHistory([]);
                  setSelectedTemplateForHistory(null);
                }}
                size="xl"
                scrollBehavior="inside"
              >
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader>
                    <HStack spacing={2}>
                      <Icon as={LuMessageCircle} color="orange.500" boxSize={5} />
                      <Text>WhatsApp Category Change History</Text>
                    </HStack>
                  </ModalHeader>
                  <ModalCloseButton />
                  <ModalBody>
                    {selectedTemplateForHistory && (
                      <VStack align="stretch" spacing={4}>
                        {/* Current Category */}
                        <Box
                          p={4}
                          bg="var(--brand-tint)"
                          border="1px solid"
                          borderColor="var(--brand-tint-strong)"
                          borderRadius="md"
                        >
                          <Flex justify="space-between" align="center">
                            <Box>
                              <Text fontSize="sm" fontWeight="medium" color="var(--brand-900)">
                                Current Category
                              </Text>
                              <Text fontSize="xs" color="var(--brand-primary-hover)" mt={1}>
                                Template: {selectedTemplateForHistory.name}
                              </Text>
                            </Box>
                            <Tag bg="var(--brand-tint-strong)" color={"#193cb8"} borderColor={"#0000001a"} border="1px solid" size="sm">
                              <HStack spacing={1}>
                                <Icon as={LuMessageCircle} boxSize={3} />
                                <Text>{selectedTemplateForHistory.WhatsAppMessageType?.name}</Text>
                              </HStack>
                            </Tag>
                          </Flex>
                        </Box>

                        {/* Change History */}
                        {historyLoading ? (
                          <Flex justify="center" align="center" minH="100px">
                            <Spinner size="lg" color="blue.500" />
                          </Flex>
                        ) : templateHistory.length > 0 ? (
                          <Box>
                            <Text fontSize="sm" fontWeight="semibold" mb={3}>
                              Change History
                            </Text>
                            <VStack align="stretch" spacing={2}>
                              {templateHistory.map((change, index) => (
                                <Box
                                  key={index}
                                  p={3}
                                  border="1px solid"
                                  borderColor="gray.200"
                                  borderRadius="md"
                                  bg="gray.50"
                                >
                                  <Flex justify="space-between" align="start">
                                    <VStack align="start" spacing={1} flex={1}>
                                      <HStack spacing={2}>
                                        <Tag
                                          colorScheme="red"
                                          size="sm"
                                          bg="red.50"
                                          color="red.700"
                                        >
                                          {/* DA-BUG-0265 — history rows may reference
                                              deleted message types; render "—" instead
                                              of crashing on null.name */}
                                          {change?.WhatsAppMessageTypeOld?.name || "—"}
                                        </Tag>
                                        <Text fontSize="xs" color="gray.500">→</Text>
                                        <Tag
                                          colorScheme="green"
                                          size="sm"
                                          bg="green.50"
                                          color="green.700"
                                        >
                                          {change?.WhatsAppMessageTypeNew?.name || "—"}
                                        </Tag>
                                      </HStack>
                                      {/* {change.reason && (
                                      <Text fontSize="xs" color="gray.600" mt={2}>
                                        <Text as="span" fontWeight="medium">Reason:</Text> {change.reason}
                                      </Text>
                                    )} */}
                                    </VStack>
                                  </Flex>
                                  <HStack spacing={3} mt={2} fontSize="xs" color="gray.500">
                                    <Text>
                                      {moment
                                        .utc(change.received_at)
                                        .tz(getUserTimeZone())
                                        .format("DD MMM YY hh:mm A")}
                                    </Text>

                                  </HStack>
                                </Box>
                              ))}
                            </VStack>
                          </Box>
                        ) : (
                          <Flex
                            direction="column"
                            align="center"
                            justify="center"
                            minH="150px"
                            color="gray.500"
                          >
                            <Icon as={LuMessageCircle} boxSize={12} opacity={0.3} mb={2} />
                            <Text fontSize="sm">No category change history available</Text>
                          </Flex>
                        )}

                        {/* Info Alert */}
                        <Box
                          p={3}
                          bg="orange.50"
                          border="1px solid"
                          borderColor="orange.200"
                          borderRadius="md"
                        >
                          <HStack spacing={2} align="start">
                            <Icon as={FaInfoCircle} color="orange.500" boxSize={4} mt={0.5} />
                            <Text fontSize="xs" color="orange.900">
                              WhatsApp category changes must comply with WhatsApp Business API guidelines.
                              Changes may require template re-approval.
                            </Text>
                          </HStack>
                        </Box>
                      </VStack>
                    )}
                  </ModalBody>

                  <ModalFooter>
                    <Button
                      onClick={() => {
                        setIsHistoryModalOpen(false);
                        setTemplateHistory([]);
                        setSelectedTemplateForHistory(null);
                      }}
                      size="sm"
                    >
                      Close
                    </Button>
                  </ModalFooter>
                </ModalContent>
              </Modal>
            </>
          )}</> : <Box className='card-style'>
          <Center h="400px">
            <Box textAlign="center">
              <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                No Access
              </Text>
              <Text fontSize="sm" color="gray.500">
                You don't have permission to view this sections.
              </Text>
            </Box>
          </Center>
        </Box>}
        <WhatsAppErrorModal
          isOpen={isWhatsAppErrorModalOpen}
          onClose={() => {
            setIsWhatsAppErrorModalOpen(false);
            setSelectedWhatsAppError(null);
          }}
          selectedTemplate={selectedWhatsAppError}
        />
      </Box>
    </>
  );
};

export default TemplateManager;